import type {
  ArtifactFile,
  StreamingEventCollection,
  ToolUsageChunk,
  ChatMessage,
} from '../types/chat.types';

export interface ContentProcessorOptions {
  isDebugMode?: boolean;
  isStreaming?: boolean;
}

/**
 * Content node types for the structured content tree.
 * - text nodes: All content (text, references, errors, debug, etc.) goes through markdown processing
 * - image nodes: Direct rendering, bypasses markdown processing
 * - tool-use nodes: Direct rendering, bypasses markdown processing
 */
export type ContentNode =
  | TextNode
  | ImageNode
  | ToolUseNode
  | ArtifactNode
  | A2UISurfaceNode;

export interface TextNode {
  type: 'text';
  content: string;
}

export interface ImageNode {
  type: 'image';
  src: string;
  alt: string;
  base64_data?: string;
  format?: string;
  filename: string;
  circuit_id: string;
  document_id: string;
}

export interface ArtifactNode {
  type: 'artifact';
  files: ArtifactFile[];
  title: string;
}

export interface A2UISurfaceNode {
  type: 'a2ui-surface';
  surfaceId: string;
  toolUseId: string;
}

export interface ToolUseNode {
  type: 'tool-use';
  id: string;
  data: ToolUseData;
  children: ContentNode[];
}

export interface ToolUseData {
  tool_use_id: string;
  name: string;
  title: string;
  status: 'pending' | 'completed';
  result: string;
  parent_tool_use_id?: string | null;
  is_error?: boolean;
  /**
   * Raw progress messages, preserved verbatim for tools with a custom
   * progress render (currently only Workflow, whose messages are JSON
   * events). Populated instead of appending text children.
   */
  progressMessages?: string[];
}

export interface ContentProcessorState {
  message: ChatMessage;
  chunks: StreamingEventCollection;
  nodes: ContentNode[];
  openRefs: string[];
  deferredRefGroups: string[];
  accumulatedRefIds: string[];
  toolUses: Record<string, ContentProcessorToolUse>;
  toolUseStack: ToolUseNode[];
  options: Required<ContentProcessorOptions>;
}

export interface ContentProcessorToolUse {
  progress: ToolUsageChunk[];
  start: ToolUsageChunk;
  stop?: ToolUsageChunk;
}

/**
 * Pre-processed content segments for efficient rendering.
 * All grouping and metadata calculation is done during content processing,
 * not during render.
 */
export type ContentSegment =
  | TextSegment
  | ImageSegment
  | ToolUseGroupSegment
  | ArtifactSegment
  | A2UISurfaceSegment;

export interface TextSegment {
  type: 'text';
  content: string;
  isLastText: boolean;
}

export interface ImageSegment {
  type: 'image';
  src: string;
  alt: string;
  circuit_id: string;
  document_id: string;
  filename: string;
}

export interface ToolUseGroupSegment {
  type: 'tool-use-group';
  toolUses: ToolUseNode[];
  toolNames: string;
  groupKey: string;
}

export interface ArtifactSegment {
  type: 'artifact';
  files: ArtifactFile[];
  title: string;
}

export interface A2UISurfaceSegment {
  type: 'a2ui-surface';
  surfaceId: string;
  toolUseId: string;
}

export const MAX_NESTING_DEPTH = 10;
